import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { ParticipantView } from '@views';
import React, { useEffect, useMemo, useState } from 'react';

type PartnerView = ParticipantView & {};

interface AccountabilityPartnerDropdownProps {
  participantId: number;
  allPartners: PartnerView[];
  selectedPartners: { participantId: number; partnerId: number }[]; // the key represents the participant,
  setSelectedPartners: (selectedPartners: { participantId: number; partnerId: number }[]) => void;
  clearPartner: boolean;
}

export const AccountabilityPartnerDropdown: React.FC<AccountabilityPartnerDropdownProps> = ({
  participantId,
  allPartners,
  selectedPartners,
  setSelectedPartners,
  clearPartner,
}) => {
  const [selectedPartner, setSelectedPartner] = useState<number>(-1);

  useEffect(() => {
    if (clearPartner) {
      setSelectedPartner(-1);
    }
  }, [clearPartner]);

  const handleChange = (e: SelectChangeEvent<number>) => {
    setSelectedPartner(Number(e.target.value));
    if (triadLocked) {
      setSelectedPartners([...selectedPartners, { participantId: participantId, partnerId: Number(e.target.value) }]);
    } else {
      setSelectedPartners([
        ...selectedPartners.filter(({ participantId: id }) => Number(id) !== participantId),
        { participantId: participantId, partnerId: Number(e.target.value) },
      ]);
    }
  };

  const totalParticipants = allPartners.length + 1; // add self back in
  const isOddGroup = totalParticipants % 2 !== 0;
  const unpairedCount = selectedPartners.filter(({ partnerId }) => partnerId === -1).length;
  const isTriadForming = isOddGroup && unpairedCount <= 3;

  // Find all unpaired participant IDs
  const unpairedParticipantIds = selectedPartners
    .filter(({ partnerId }) => partnerId === -1)
    .map(({ participantId }) => Number(participantId));

  const [triadLocked, setTriadLocked] = useState(false);

  // Track the first time triadForming becomes true
  useEffect(() => {
    if (isTriadForming && !triadLocked) {
      setTriadLocked(true);
    }
  }, [isTriadForming, triadLocked]);

  const deps = triadLocked ? [] : [allPartners, selectedPartners, unpairedParticipantIds];
  const availablePartners = useMemo(() => {
    const isCurrentInTriad = isTriadForming && unpairedParticipantIds.includes(participantId);

    return allPartners.filter((partner) => {
      if (isTriadForming && isCurrentInTriad) {
        // Only allow selection of the other 2 triad members
        return unpairedParticipantIds.includes(partner.id);
      }

      // Default case: participant not already selected by someone else
      return !selectedPartners.some(
        ({ participantId: id, partnerId: selectedId }) => Number(id) !== participantId && selectedId === partner.id
      );
    });
  }, [deps]);

  return (
    <>
      {/*<DebugPanel displayItem={{ totalParticipants, isOddGroup, unpairedCount }}></DebugPanel>*/}
      <FormControl fullWidth>
        <Select<number> defaultValue={-1} value={selectedPartner} onChange={handleChange} size={'small'}>
          <MenuItem value={-1}>Select an Accountability Partner</MenuItem>
          {availablePartners.map((partner) => (
            <MenuItem key={partner.id} value={partner.id}>
              {partner.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};
